import { useEffect, useState, useRef } from 'react';

/**
 * Returns value after a specified time period has passed without any further changes
 *
 * @param {T} value - value to be debounced
 * @param {number} delay - wait time before updating debounce value
 * @param {object} options - set of options to tweak behavior
 * @param {boolean} options.instantClear - with !value, instantly update debouncedValue
 * @returns {T} debouncedValue
 */
export default function useDebounce<T>(value: T, delay: number, options?: { instantClear?: boolean }): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!value && options?.instantClear) {
      setDebouncedValue(value);
    } else {
      timeout.current = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
    }

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, [value, delay, options?.instantClear]);

  return debouncedValue;
}
